import { useState, useMemo } from 'react';
// ;
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Stack,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import MapIcon from '@mui/icons-material/Map';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listSettings,
  getCategories,
  updateSetting,
  initializeDefaultSettings,
  Setting,
  SettingUpdate
} from '../services/settingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [initDialog, setInitDialog] = useState(false);

  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['settings-categories'],
    queryFn: getCategories
  });

  const { data: settings = [], isLoading: settingsLoading, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: () => listSettings()
  });

  const updateMut = useMutation({
    mutationFn: ({ key, data }: { key: string; data: SettingUpdate }) => updateSetting(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setEditingKey(null);
      setEditValues({});
    }
  });

  const initMut = useMutation({
    mutationFn: initializeDefaultSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setInitDialog(false);
    }
  });

  const categorizedSettings = useMemo(() => {
    const result: Record<string, Setting[]> = {};
    settings.forEach(setting => {
      if (!result[setting.category]) {
        result[setting.category] = [];
      }
      result[setting.category].push(setting);
    });
    return result;
  }, [settings]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'infrastructure':
        return <StorageIcon />;
      case 'business':
        return <BusinessIcon />;
      case 'application':
        return <CodeIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'infrastructure':
        return 'primary';
      case 'business':
        return 'secondary';
      case 'application':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleEdit = (setting: Setting) => {
    setEditingKey(setting.key);
    setEditValues({ [setting.key]: setting.value });
  };

  const handleSave = (setting: Setting) => {
    const newValue = editValues[setting.key];
    updateMut.mutate({
      key: setting.key,
      data: { value: newValue }
    });
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValues({});
  };

  const handleValueChange = (key: string, value: any) => {
    setEditValues(prev => ({ ...prev, [key]: value }));
  };

  const renderValueEditor = (setting: Setting) => {
    const value = editValues[setting.key] ?? setting.value;

    if (setting.is_sensitive) {
      return (
        <TextField
          type="password"
          value={value}
          onChange={(e) => handleValueChange(setting.key, e.target.value)}
          fullWidth
          size="small"
          disabled={setting.is_required}
        />
      );
    }

    switch (setting.data_type) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => handleValueChange(setting.key, e.target.checked)}
                disabled={setting.is_required}
              />
            }
            label={value ? t('settings.enabled') : t('settings.disabled')}
          />
        );

      case 'integer':
        return (
          <TextField
            type="number"
            value={value}
            onChange={(e) => handleValueChange(setting.key, parseInt(e.target.value) || 0)}
            fullWidth
            size="small"
            disabled={setting.is_required}
          />
        );

      case 'json':
        return (
          <TextField
            multiline
            rows={4}
            value={JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleValueChange(setting.key, parsed);
              } catch {
                // Invalid JSON, don't update
              }
            }}
            fullWidth
            size="small"
            disabled={setting.is_required}
            sx={{ fontFamily: 'monospace' }}
          />
        );

      default:
        return (
          <TextField
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            fullWidth
            size="small"
            disabled={setting.is_required}
          />
        );
    }
  };

  const renderValueDisplay = (setting: Setting) => {
    if (setting.is_sensitive) {
      return <Typography>***</Typography>;
    }

    switch (setting.data_type) {
      case 'boolean':
        return (
          <Chip 
            label={setting.value ? t('settings.enabled') : t('settings.disabled')} 
            color={setting.value ? 'success' : 'default'}
            size="small"
          />
        );

      case 'json':
        return (
          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
            <pre>{JSON.stringify(setting.value, null, 2)}</pre>
          </Box>
        );

      default:
        return <Typography>{String(setting.value)}</Typography>;
    }
  };

  const renderSettingCard = (setting: Setting) => {
    const isEditing = editingKey === setting.key;

    return (
      <Card key={setting.key} sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box flex={1}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="h6">{setting.name}</Typography>
                {setting.is_required && (
                  <Chip label={t('settings.required')} color="error" size="small" />
                )}
                {setting.is_sensitive && (
                  <Chip label={t('settings.sensitive')} color="warning" size="small" />
                )}
              </Stack>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {setting.description}
              </Typography>
              
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Key: {setting.key} | Type: {setting.data_type}
              </Typography>

              <Box mt={2}>
                {isEditing ? renderValueEditor(setting) : renderValueDisplay(setting)}
              </Box>
            </Box>

            <Stack direction="row" spacing={1}>
              {isEditing ? (
                <>
                  <Tooltip title="Save">
                    <IconButton 
                      onClick={() => handleSave(setting)}
                      color="primary"
                      disabled={updateMut.isPending}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton onClick={handleCancel}>
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Edit">
                  <IconButton 
                    onClick={() => handleEdit(setting)}
                    disabled={setting.is_required && !['business'].includes(setting.category)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (categoriesLoading || settingsLoading) {
    return <LinearProgress />;
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('settings.title')}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            {t('settings.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => setInitDialog(true)}
          >
            {t('settings.initializeDefaults')}
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t('settings.configureInfo')}
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label={t('settings.allSettings')} />
          {categories.map((category, _) => (
            <Tab 
              key={category} 
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  {getCategoryIcon(category)}
                  <span style={{ textTransform: 'capitalize' }}>{category}</span>
                </Stack>
              }
            />
          ))}
          <Tab 
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <MapIcon />
                <span>{t('settings.mapTest.title')}</span>
              </Stack>
            }
          />
        </Tabs>
      </Box>

      <TabPanel value={selectedTab} index={0}>
        {categories.map((category) => (
          <Accordion key={category} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={2}>
                {getCategoryIcon(category)}
                <Typography variant="h6">
                  {t('settings.categorySettings', { category: t(`settings.${category}`) })}
                </Typography>
                <Chip 
                  label={`${categorizedSettings[category]?.length || 0} ${t('settings.items')}`} 
                  size="small"
                  color={getCategoryColor(category) as any}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {categorizedSettings[category]?.map(renderSettingCard)}
            </AccordionDetails>
          </Accordion>
        ))}
      </TabPanel>

      {categories.map((category, index) => (
        <TabPanel key={category} value={selectedTab} index={index + 1}>
          <Typography variant="h5" gutterBottom>
            {t('settings.categorySettings', { category: t(`settings.${category}`) })}
          </Typography>
          {categorizedSettings[category]?.map(renderSettingCard)}
        </TabPanel>
      ))}

      {/* Map Test Tab */}
      <TabPanel value={selectedTab} index={categories.length + 1}>
        <Box>
          <Typography variant="h5" gutterBottom>
            {t('settings.mapTest.title')}
          </Typography>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="body1" paragraph>
                {t('settings.mapTest.description')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<MapIcon />}
                onClick={() => navigate('/map-test')}
                size="large"
              >
                {t('settings.mapTest.openMapTest')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Initialize Defaults Dialog */}
      <Dialog open={initDialog} onClose={() => setInitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settings.initializeDefaultsTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('settings.initializeDefaultsDescription')}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('settings.initializeDefaultsWarning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInitDialog(false)}>{t('common.cancel')}</Button>
          <Button 
            onClick={() => initMut.mutate()} 
            variant="contained"
            disabled={initMut.isPending}
          >
            {initMut.isPending ? t('settings.initializing') : t('settings.initialize')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage; 